import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons';
import Alert from '@v2/shared/components/Alert';
import Button from '@v2/shared/components/Button';
import { FormInput, FormSelect, FormTextArea } from '@v2/shared/components/Form';
import useContactForm, { FORM_TYPE } from './useContactForm';
import { FIELD_MESSAGES, OPTIONAL_PLACEHOLDER } from './constants';
import type { ContactPageProps } from './types';

/**
 * The contact form, posting into the same `contact.php?xCmd=send` branch the legacy form
 * posted into — over fetch when it can, the way DiscountMags' bulk order quote form does,
 * so a failure can be shown in place instead of reloading the page.
 *
 * It keeps a real `action` and `method` all the same, and the legacy names on its inputs,
 * because the page is server-rendered: the form is on screen and submittable before the
 * bundle has hydrated, and it is the whole page for a visitor with scripting off. Without
 * them the browser's own submit is a GET to this url — the message is never sent, nothing
 * says so, and the whole of it ends up in the address bar, the access log and the
 * referrer. react-hook-form's handleSubmit cancels the native submit once it is running,
 * so the scripted path is unaffected.
 *
 * The five fields are written out rather than looped over `jss_customer_fields`, which is
 * what the legacy template did. Same reasoning as the quote form beside it: these are the
 * five fields the simple contact form has had for years, they are the ones contact.php
 * validates by name on the way back in, and a generic renderer would have to reimplement
 * that table's validation vocabulary in TypeScript to say what five `register` calls say
 * here. Their labels and messages are the table's own, so nothing a visitor reads changed.
 */
export default function ContactForm({ subjects, customer, sent }: ContactPageProps) {
  const {
    register,
    handleSubmit,
    errors,
    onSubmit,
    action,
    submitState,
    submitMessage,
    isSubmitting,
  } = useContactForm({ customer, sent });

  if (submitState === 'success') {
    return (
      <Alert color="success" className="mb-0">
        <p className="m-0 font-semibold">
          Thanks for contacting us! We&apos;ll get back to you within a couple business days.
        </p>
      </Alert>
    );
  }

  return (
    <form
      action={action}
      method="post"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="max-w-xl"
    >
      <input type="hidden" name="formType" value={FORM_TYPE} />

      {submitState === 'error' && submitMessage && <Alert color="danger">{submitMessage}</Alert>}

      <FormInput
        id="name"
        label="Your name"
        type="text"
        maxLength={100}
        disabled={isSubmitting}
        error={errors.name?.message}
        {...register('name', { required: FIELD_MESSAGES.name })}
      />

      <FormInput
        id="EmailAddress"
        label="Your Email Address"
        type="email"
        maxLength={250}
        disabled={isSubmitting}
        error={errors.EmailAddress?.message}
        {...register('EmailAddress', {
          required: FIELD_MESSAGES.EmailAddress,
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: FIELD_MESSAGES.EmailAddress,
          },
        })}
      />

      <FormInput
        id="order"
        label="Your order number"
        type="text"
        maxLength={20}
        placeholder={OPTIONAL_PLACEHOLDER}
        disabled={isSubmitting}
        {...register('order')}
      />

      <FormSelect
        id="Subject"
        label="Subject"
        disabled={isSubmitting}
        error={errors.Subject?.message}
        {...register('Subject', { required: FIELD_MESSAGES.Subject })}
      >
        <option value="">&nbsp;</option>
        {subjects.map((subject) => (
          <option key={subject.id} value={subject.id}>
            {subject.title}
          </option>
        ))}
      </FormSelect>

      <FormTextArea
        id="cfComments"
        label="Message"
        rows={10}
        maxLength={10240}
        disabled={isSubmitting}
        error={errors.cfComments?.message}
        {...register('cfComments', { required: FIELD_MESSAGES.cfComments })}
      />

      <Button
        type="submit"
        isLoading={isSubmitting}
        loadingText="Sending…"
        size="sm"
        shape="rounded"
        icon={faPaperPlane}
      >
        Send message
      </Button>
    </form>
  );
}
